import { Fragment } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { intervalToDuration } from "date-fns";
import { FaCircle, FaClipboard, FaClock, FaPaperPlane } from "react-icons/fa";

const FOCUS_CONTROL_TYPES = [
  { icon: FaCircle, color: "#a4b7c1", titleId: "FocusControlOff" },
  { icon: FaClipboard, color: "#63c2de", titleId: "FocusControlLog" },
  { icon: FaClock, color: "#ffc107", titleId: "FocusControlDelay" },
  { icon: FaPaperPlane, color: "#f86c6b", titleId: "FocusControlSubmit" },
];

const RUN_COLORS = [
  { background: "#dbeafe", color: "#1e40af" },
  { background: "#dcfce7", color: "#166534" },
];

function roundUpDurationMs(ms) {
  const numericMs = Number(ms) || 0;
  return Math.ceil(numericMs / 1000) * 1000;
}

function formatGroupDuration(ms) {
  const {
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = intervalToDuration({ start: 0, end: ms });

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;

  return `${seconds}s`;
}

function FocusLossModal({
  isOpen,
  onClose,
  runData,
  label,
  moduleName,
  isFinalExam,
  intl,
}) {
  const groups = [];
  let currentGroup = null;
  runData.forEach((row) => {
    if (!currentGroup || currentGroup.runNumber !== row.runNumber) {
      currentGroup = {
        runNumber: row.runNumber,
        rows: [],
        totalMs: 0,
        focusControlType: row.focusControlType,
      };
      groups.push(currentGroup);
    } else if (row.focusControlType !== currentGroup.focusControlType) {
      console.warn(
        `FC type mismatch: run ${row.runNumber}, ${label}${moduleName ? `, ${moduleName}` : ""} (expected ${currentGroup.focusControlType}, got ${row.focusControlType})`,
      );
    }
    currentGroup.rows.push(row);
    currentGroup.totalMs += roundUpDurationMs(row.durationMs);
  });

  const sortedGroups = [...groups].sort(
    (a, b) => Number(b.runNumber) - Number(a.runNumber),
  );

  let nextIndex = 1;
  sortedGroups.forEach((group) => {
    group.startIndex = nextIndex;
    nextIndex += group.rows.length;
  });

  const totalDuration = formatGroupDuration(
    sortedGroups.reduce((sum, group) => sum + group.totalMs, 0),
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      modalClassName="focus-loss-modal"
    >
      <ModalHeader toggle={onClose} className="focus-loss-modal__header">
        <div className="focus-loss-modal__title">{label}</div>
        {moduleName && (
          <div className="focus-loss-modal__subtitle">{moduleName}</div>
        )}
      </ModalHeader>
      <ModalBody className="focus-loss-modal__body">
        <div className="focus-loss-modal__stats">
          <div className="focus-loss-modal__stat-card">
            <div className="focus-loss-modal__stat-label">
              <FormattedMessage id="FocusLostCount" />
            </div>
            <div className="focus-loss-modal__stat-value">{runData.length}</div>
          </div>
          <div className="focus-loss-modal__stat-card">
            <div className="focus-loss-modal__stat-label">
              <FormattedMessage id="TotalDuration" />
            </div>
            <div className="focus-loss-modal__stat-value">{totalDuration}</div>
          </div>
        </div>
        <div className="focus-loss-modal__table-wrap">
          <table
            className={`table table-borderless mb-0 focus-loss-modal__table ${
              isFinalExam ? "focus-loss-modal__table--final" : ""
            }`.trim()}
          >
            <thead>
              <tr>
                {isFinalExam ? (
                  <th className="focus-loss-modal__index-cell">#</th>
                ) : (
                  <th className="focus-loss-modal__run-cell">
                    <FormattedMessage id="Run" />
                  </th>
                )}
                <th className="focus-loss-modal__setting-cell">
                  <FormattedMessage id="FocusControlSetting" />
                </th>
                <th className="text-end focus-loss-modal__time-cell focus-loss-modal__time-header">
                  <FormattedMessage id="FocusLostAt" />
                </th>
                <th className="text-end focus-loss-modal__duration-cell">
                  <FormattedMessage id="Duration" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((group, groupIdx) => {
                const colorStyle = RUN_COLORS[groupIdx % RUN_COLORS.length];
                return (
                  <Fragment key={group.runNumber}>
                    {group.rows.map((row, rowIdx) => (
                      <tr key={`${group.runNumber}-${rowIdx}`}>
                        {isFinalExam && (
                          <td
                            className={`focus-loss-modal__index-cell ${
                              rowIdx === 0
                                ? "focus-loss-modal__section-top"
                                : ""
                            }`.trim()}
                          >
                            {group.startIndex + rowIdx}
                          </td>
                        )}
                        {!isFinalExam && rowIdx === 0 && (
                          <td
                            className="focus-loss-modal__run-cell focus-loss-modal__section-top"
                            rowSpan={group.rows.length + 1}
                          >
                            <span
                              className="focus-loss-modal__run-pill"
                              style={colorStyle}
                            >
                              {row.runNumber}
                            </span>
                          </td>
                        )}
                        {rowIdx === 0 &&
                          (() => {
                            const fcType =
                              FOCUS_CONTROL_TYPES[group.focusControlType] ||
                              FOCUS_CONTROL_TYPES[0];
                            const Icon = fcType.icon;
                            return (
                              <td
                                className="focus-loss-modal__setting-cell focus-loss-modal__section-top"
                                rowSpan={group.rows.length + 1}
                              >
                                <span
                                  className="focus-loss-modal__setting-content"
                                  style={{ color: fcType.color }}
                                >
                                  <Icon />
                                  <FormattedMessage id={fcType.titleId} />
                                </span>
                              </td>
                            );
                          })()}
                        <td
                          className={`focus-loss-modal__time-cell text-end ${
                            rowIdx === 0 ? "focus-loss-modal__section-top" : ""
                          }`.trim()}
                        >
                          {row.formattedTime}
                        </td>
                        <td
                          className={`text-end focus-loss-modal__duration-cell ${
                            rowIdx === 0 ? "focus-loss-modal__section-top" : ""
                          }`.trim()}
                        >
                          <span
                            className="focus-loss-modal__duration-pill"
                            style={colorStyle}
                          >
                            {row.autoSubmit && (
                              <FaPaperPlane
                                title={intl.formatMessage({ id: "FocusControlAutoSubmitted" })}
                                className="text-danger me-1"
                              />
                            )}
                            {row.formattedDuration}
                          </span>
                        </td>
                      </tr>
                    ))}
                    <tr className="focus-loss-modal__run-total-row">
                      {isFinalExam && (
                        <td className="focus-loss-modal__index-cell"></td>
                      )}
                      <td className="focus-loss-modal__time-cell text-end">
                        <span className="focus-loss-modal__run-total-label">
                          <FormattedMessage id="TotalDuration" />
                        </span>
                      </td>
                      <td className="focus-loss-modal__duration-cell text-end">
                        <span className="focus-loss-modal__duration-pill">
                          {formatGroupDuration(group.totalMs)}
                        </span>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default injectIntl(FocusLossModal);
